import { useState } from "react";
import { Bounce, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import LoginPage from "./pages/auth/LoginPage";
import BannerListPage from "./pages/banners/BannerListPage";
import BannerPage from "./pages/banners/BannerPage";
import CreateBannerPage from "./pages/banners/CreateBannerPage";
import UpdateBannerPage from "./pages/banners/UpdateBannerPage";
import HomePage from "./pages/common/HomePage";
import PageNotFound from "./pages/common/PageNotFound";
import NotificationList from "./pages/notification/notificationList";
import NotificationPage from "./pages/notification/NotificationPage";
import SendNotification from "./pages/notification/SendNotification";
import PrivacyPage from "./pages/privacyPolicy/privacyPage";
import PrivacyPolicy from "./pages/privacyPolicy/PrivacyPolicy";
import UpdatePrivacy from "./pages/privacyPolicy/UpdatePrivacy";
import UserListPage from "./pages/users/UserListPage";
import AddUserPage from "./pages/users/AddUserPage";
import UsersStatisticsPage from "./pages/users/UsersStatisticsPage";
import Videos from "./pages/video/Video";
import VideoList from "./pages/videoUpload/VideoList";
import CategoryPage from "./pages/Category/CategoryPage";
import SubCategoryPage from "./pages/SubCategory/SubCategoryPage";
import Category from "./pages/Category/Category";
import VideoUploadPage from "./pages/videoUpload/VideoUploadPage";
import AddCategory from "./pages/Category/AddCategory";
import SubCategory from "./pages/SubCategory/SubCategory";
import AddSubCategory from "./pages/SubCategory/AddSubCategory";
import EditVideo from "./pages/videoUpload/EditVideo";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Phase 1 - New Modules
import FeedbackPage from "./pages/feedback/index";
import FeedbackListPage from "./pages/feedback/FeedbackListPage";
import EmergencyContactsPage from "./pages/emergencyContacts/EmergencyContactsPage";
import SupportTicketsPage from "./pages/support/SupportTicketsPage";
import CommentsPage from "./pages/comments/index";
import CommentsListPage from "./pages/comments/CommentsListPage";

// Phase 2 - New Modules
import WeatherPage from "./pages/weather/WeatherPage";

// Phase 3 - New Modules
import GovernmentSchemesLayout from "./pages/governmentSchemes/index";
import SchemeListPage from "./pages/governmentSchemes/SchemeListPage";
import SchemeFormPage from "./pages/governmentSchemes/SchemeFormPage";
import FarmDiaryLayout from "./pages/farmDiary/index";
import FarmDiaryPage from "./pages/farmDiary/FarmDiaryPage";
import FarmDairyLayout from "./pages/farmDairy/index";
import FarmDairyListPage from "./pages/farmDairy/FarmDairyListPage";
import FarmDairyDetailPage from "./pages/farmDairy/FarmDairyDetailPage";
import DashboardsLayout from "./pages/dashboards/index";
import EngagementDashboard from "./pages/dashboards/EngagementDashboard";
import VideoPerformanceDashboard from "./pages/dashboards/VideoPerformanceDashboard";

// Phase 4 - Admin User Management
import AdminUsersListPage from "./pages/adminUsers/AdminUsersListPage";
import AdminUserFormPage from "./pages/adminUsers/AdminUserFormPage";
import AccountPage from "./pages/account/AccountPage";
import DashboardPage from "./pages/dashboard/DashboardPage";
import { isAdminRole } from "./utils/auth";

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>

      <Route path="/" element={<HomePage login={isAuthenticated} />}>
        {isAuthenticated ? (
          <>
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/users-statistics" element={<UsersStatisticsPage />} />
            <Route path="/users" element={<UserListPage />} />
            <Route path="/users/add" element={<AddUserPage />} />
            <Route path="/videos" element={<Videos />} />
            <Route path="/privacypolicy" element={<PrivacyPage />}>
              <Route index path="" element={<PrivacyPolicy />} />
              <Route index path="update" element={<UpdatePrivacy />} />
            </Route>
            <Route path="/videoUpload" element={<VideoUploadPage />}>
              <Route index path="" element={<VideoList />} />
              <Route path="upload" element={<EditVideo />} />
              <Route path=":id" element={<EditVideo />} />
            </Route>
            <Route path="/category" element={<CategoryPage />}>
              <Route index path="" element={<Category />} />
              <Route index path="add" element={<AddCategory />} />
              <Route index path=":id" element={<AddCategory />} />
            </Route>
            <Route path="/subCategory" element={<SubCategoryPage />}>
              <Route index path="" element={<SubCategory />} />
              <Route index path="add" element={<AddSubCategory />} />
              <Route index path=":id" element={<AddSubCategory />} />
            </Route>
            <Route path="/notifications" element={<NotificationPage />}>
              <Route index path="" element={<NotificationList />} />
              <Route index path="send" element={<SendNotification />} />
            </Route>
            <Route path="/banners" element={<BannerPage />}>
              <Route index element={<BannerListPage />} />
              <Route path="create" element={<CreateBannerPage />} />
              <Route path=":id" element={<UpdateBannerPage />} />
            </Route>

            <Route path="/feedback" element={<FeedbackPage />}>
              <Route index path="" element={<FeedbackListPage />} />
            </Route>
            <Route path="/comments" element={<CommentsPage />}>
              <Route path=":videoId" element={<CommentsListPage />} />
            </Route>
            <Route path="/support" element={<SupportTicketsPage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/emergency-contacts" element={<EmergencyContactsPage />} />
            <Route path="/weather" element={<WeatherPage />} />

            <Route path="/government-schemes" element={<GovernmentSchemesLayout />}>
              <Route index path="" element={<SchemeListPage />} />
              <Route path="new" element={<SchemeFormPage />} />
              <Route path=":id/edit" element={<SchemeFormPage />} />
            </Route>

            <Route path="/farm-diary" element={<FarmDiaryLayout />}>
              <Route index path="" element={<FarmDiaryPage />} />
            </Route>

            <Route path="/farm-dairy" element={<FarmDairyLayout />}>
              <Route index element={<FarmDairyListPage />} />
              <Route path=":userId" element={<FarmDairyDetailPage />} />
            </Route>

            <Route path="/dashboards" element={<DashboardsLayout />}>
              <Route index path="" element={<Videos />} />
              <Route path="analysis" element={<Videos />} />
              <Route path="engagement" element={<EngagementDashboard />} />
              <Route path="performance" element={<VideoPerformanceDashboard />} />
            </Route>

            <Route
              path="/admin-users"
              element={isAdminRole() ? <AdminUsersListPage /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="/admin-users/new"
              element={isAdminRole() ? <AdminUserFormPage /> : <Navigate to="/dashboard" replace />}
            />
            <Route
              path="/admin-users/:id"
              element={isAdminRole() ? <AdminUserFormPage /> : <Navigate to="/dashboard" replace />}
            />

            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<PageNotFound />} />
          </>
        ) : (
          <>
            <Route index element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        )}
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <>
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
      <ErrorBoundary fallback={null}>
        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          transition={Bounce}
        />
      </ErrorBoundary>
    </>
  );
}

export default App;
